import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Payment } from "@b2bportal/purchase-api";
import type {
  PackageItinerary,
  PaymentBreakdown,
} from "@b2bportal/rbc-bundled-packages-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import { AccountSelectors } from "@/modules/account";

function buildCheckoutSubtitle(
  hotelData: any,
  flightData: any,
  hasGround: boolean,
  t: (key: string, params?: Record<string, string | number>) => string
): string {
  const parts: string[] = [];

  const reservation = hotelData?.reservation;
  if (reservation?.checkInDate && reservation?.checkOutDate) {
    const nights = dayjs(reservation.checkOutDate).diff(
      dayjs(reservation.checkInDate),
      "day"
    );
    if (nights > 0) {
      parts.push(t("packageTrips.checkoutSubtitle.stay", { count: nights }));
    }
  } else if (hotelData) {
    parts.push(t("packageTrips.checkoutSubtitle.stayOnly"));
  }

  if (flightData) {
    const passengers = flightData?.bookedItinerary?.passengers;
    const alone = passengers?.alone?.length ?? 0;
    const withLapInfants = passengers?.withLapInfants?.length ?? 0;
    const totalPassengers = alone + withLapInfants * 2;

    if (totalPassengers > 0) {
      parts.push(
        t("packageTrips.checkoutSubtitle.flight", { count: totalPassengers })
      );
    } else {
      parts.push(t("packageTrips.checkoutSubtitle.flightOnly"));
    }
  }

  if (hasGround) {
    parts.push(t("packageTrips.checkoutSubtitle.car"));
  }

  if (parts.length === 0) return "";

  if (parts.length === 1) {
    return t("packageTrips.checkoutSubtitle.pricesInclude", {
      items: parts[0],
    });
  }
  const last = parts[parts.length - 1];
  return t("packageTrips.checkoutSubtitle.pricesInclude", {
    items: `${parts.slice(0, -1).join(", ")} ${t("flightTrips.and")} ${last}`,
  });
}

interface UsePackageCheckoutBreakdownArgs {
  pricingBreakdown: PackageItinerary["pricingBreakdown"];
  paymentBreakdown: PaymentBreakdown | undefined;
  hotelData: any;
  flightData: any;
  hasGround: boolean;
}

export const usePackageCheckoutBreakdown = ({
  pricingBreakdown,
  paymentBreakdown,
  hotelData,
  flightData,
  hasGround,
}: UsePackageCheckoutBreakdownArgs) => {
  const { t, formatFiatCurrency, formatRewardsCurrency, formatNumber } =
    useI18nContext();
  const rewardsAccountId = useSelector(AccountSelectors.getRewardsAccountId);

  return useMemo(() => {
    const total = pricingBreakdown?.total?.fiat;

    const fixedRewardsPrice = rewardsAccountId
      ? pricingBreakdown?.tenantSpecific?.accountSpecific?.[rewardsAccountId]
      : undefined;
    const fixedPoints = fixedRewardsPrice?.value ?? 0;
    const subtitle = buildCheckoutSubtitle(hotelData, flightData, hasGround, t);

    const paymentLineItems = paymentBreakdown?.paymentLineItems;

    const rewardsPayment = paymentLineItems?.find(
      ({ type }) => (type as string) === Payment.RbcAvionPoints
    );

    const cardPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.Card
    );

    const creditPayment = paymentLineItems?.find(
      ({ type }) => (type as string) === Payment.TravelWalletCredit
    );

    const numberOfFlexiblePoints =
      rewardsPayment?.value?.pointsRedemptionBreakdown
        ?.numberOfFlexiblePoints ?? 0;

    const pointsRedeemed =
      numberOfFlexiblePoints > 0
        ? formatNumber(numberOfFlexiblePoints, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        : undefined;

    const flexFiat =
      rewardsPayment?.value?.pointsRedemptionBreakdown
        ?.flexiblePointsCashEquivalent;
    const pointsRedeemedFiat = flexFiat?.value
      ? `-${formatFiatCurrency({
          value: flexFiat.value,
          currencyCode: flexFiat.currencyCode ?? total?.currencyCode ?? "CAD",
        })}`
      : undefined;

    const creditAmount = creditPayment?.value?.paymentAmount?.amount ?? 0;
    const travelCreditApplied =
      creditPayment && creditAmount > 0
        ? `-${formatFiatCurrency({
            value: creditAmount,
            currencyCode:
              creditPayment.value?.paymentAmount?.currency ??
              total?.currencyCode ??
              "CAD",
          })}`
        : undefined;

    const amountPaid = formatFiatCurrency({
      value: cardPayment?.value?.paymentAmount?.amount ?? 0,
      currencyCode:
        cardPayment?.value?.paymentAmount?.currency ??
        total?.currencyCode ??
        "CAD",
    });

    const paymentMethod = cardPayment?.value?.cardNumberDisplay ?? undefined;

    const payLater = pricingBreakdown?.payLaterAmount?.fiat;
    const hasPayLater = payLater != null && payLater.value > 0;

    const totalLabel =
      fixedPoints > 0 && total
        ? t("packageTrips.totalWithFixedPoints", {
            points: formatRewardsCurrency(
              { value: fixedPoints, currency: fixedRewardsPrice.currency },
              { minimumFractionDigits: 0, maximumFractionDigits: 0 }
            ),
            cash: formatFiatCurrency(total),
          })
        : total
          ? formatFiatCurrency(total)
          : undefined;

    return {
      subtitle,
      total: totalLabel,
      totalFiat: total,
      pointsRedeemed,
      pointsRedeemedFiat,
      travelCreditApplied,
      amountPaid,
      paymentMethod,
      hasPayLater,
      payLater: hasPayLater ? formatFiatCurrency(payLater) : undefined,
      payLaterCurrencyCode: payLater?.currencyCode,
    };
  }, [
    pricingBreakdown,
    paymentBreakdown,
    hotelData,
    flightData,
    hasGround,
    rewardsAccountId,
    t,
    formatFiatCurrency,
    formatRewardsCurrency,
    formatNumber,
  ]);
};
